import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import client, { API } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import MagazineLayout from "@/components/MagazineLayout";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import BookCall from "@/components/BookCall";
import JoinFaq from "@/components/JoinFaq";
import { VALUE_STACK, VALUE_TOTAL } from "@/lib/valueStack";
import { cycleIndexFor } from "@/lib/issue";
import { Check, ArrowRight, PhoneCall, Play } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  "A permanent Cover Member profile — your always-on presence in the magazine",
  "A permanent Directory listing, SEO + GEO optimised to be found",
  "The content AI search engines actually read and recommend — TikToks can't be quoted, your feature can",
  "A narrated feature — readers can listen to your story on the go",
  "A ready-to-post reel of your feature — scrolling press-page or photo film, your pick of shots",
  "Recommended by the Ask S/ME concierge when readers ask who to hire",
  "An Editorial Feature — your story written by the S/ME editorial desk",
  "A Spotlight Feature — or the Cover Star slot fronting the homepage",
  "A signature S/ME cover shoot from your own photos",
  "A rapid-fire \"5 Questions\" published interview",
  "One Premium Advert, placed naturally through the magazine",
  "A feature in the S/ME newsletter — mailed straight to reader inboxes",
  "Reels, Stories & social tiles for Instagram, Facebook + TikTok — posted with your handle tagged and a collaborate invite, so they land on your grid too",
  "A blog feature each year + unlimited Noticeboard offers",
  "One fresh moment in the magazine every month, spread fairly across the year",
  "Editor review on everything — quality guaranteed",
];

export default function Join() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("yearly");
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);

  // resume after stripe redirect + guest pass redemption
  useEffect(() => {
    document.title = "Join — S/ME";
    const sp = new URLSearchParams(window.location.search);
    const sid = sp.get("session_id");
    if (sid) { navigate(`/portal?session_id=${sid}`); return; }
    const pass = sp.get("pass");
    if (pass && !loading) {
      if (!user) { navigate(`/register?next=${encodeURIComponent(`/join?pass=${pass}`)}`); return; }
      if (user.role === "admin") { toast("That's a guest invite link — you're the editor, so it stays unused. Send it to your guest."); navigate("/admin"); return; }
      client.post("/invites/redeem", { token: pass })
        .then(async () => { navigate("/portal?focus=profile"); await refresh(); toast.success("Welcome to S/ME — your membership is active. Complete your profile and send it to the editorial desk."); })
        .catch((e) => toast.error(e?.response?.data?.detail || "This invite link isn't valid any more."));
    }
  }, [navigate, user, loading]);

  const startCheckout = async () => {
    if (!user) { navigate("/login?next=/join"); return; }
    if (!agreedTerms) { toast.error("Please tick the box to agree to the Membership Terms first."); return; }
    setLoadingCheckout(true);
    try {
      const { data } = await client.post("/checkout/session", { plan, origin_url: window.location.origin, agreed_terms: true });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoadingCheckout(false);
    }
  };

  return (
    <MagazineLayout>
      <Seo
        title="Join — S/ME"
        description="The best-value visibility membership for UK SMEs. Editorial features, photography, adverts, social and SEO — £30 a month or £300 a year."
      />
      <div className="pt-[68px] bg-ink text-cream min-h-screen grain">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-16 sm:py-24 relative z-10">
          <Reveal>
            <p className="overline text-gloss-gold mb-5">Membership</p>
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.9] max-w-4xl">
              Get seen. Get clients. Become a star.
            </h1>
            <p className="font-sans text-lg sm:text-xl text-cream/75 mt-6 max-w-2xl leading-relaxed">
              Premium, affordable visibility for the UK's independent businesses — no expensive ads, no SEO agency. Just beautiful editorial that makes readers fall for you.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#plans" className="inline-flex items-center gap-2.5 bg-gloss-gold text-ink px-6 py-3.5 overline hover:bg-cream transition-colors" data-testid="hero-join-now">
                Join now — from £30 a month ↓
              </a>
              <a href="#book-call" className="inline-flex items-center gap-2.5 border border-gloss-gold text-gloss-gold px-5 py-3.5 overline hover:bg-gloss-gold hover:text-ink transition-colors" data-testid="hero-book-call">
                <PhoneCall size={14} /> Book a free discovery call ↓
              </a>
              <a href="#demo" className="inline-flex items-center gap-2.5 border border-cream/30 px-5 py-3.5 overline hover:border-gloss-gold hover:text-gloss-gold transition-colors" data-testid="hero-watch-demo">
                <Play size={14} /> Watch the 90-second demo
              </a>
            </div>
            <Link to="/membership" className="inline-flex items-center gap-2 mt-6 overline text-gloss-gold link-underline" data-testid="see-membership-year">
              See everything you get across the year →
            </Link>
          </Reveal>

          {/* The 90-second film */}
          <Reveal delay={0.04}>
            <div id="demo" className="mt-12 scroll-mt-24" data-testid="demo-film-section">
              <p className="overline text-gloss-gold mb-3">Watch — what membership looks like · 90 seconds</p>
              <video controls preload="metadata" playsInline poster={`${API}/files/the-glossy/productions/demo/sme-demo-poster.jpg`}
                className="w-full max-w-3xl border border-cream/20 bg-black aspect-video" data-testid="demo-film">
                <source src={`${API}/files/the-glossy/productions/demo/sme-demo.mp4`} type="video/mp4" />
              </video>
            </div>
          </Reveal>

          {/* FOUNDING MEMBER — preview period offer */}
          {cycleIndexFor() < 0 && (
            <Reveal delay={0.05}>
              <div className="mt-10 border border-gloss-gold bg-gloss-gold/10 p-6 sm:p-8" data-testid="founding-offer">
                <p className="overline text-gloss-gold mb-2">September launch — join now</p>
                <p className="font-serif text-2xl sm:text-3xl tracking-tight leading-tight">Pay today. Your year starts with the September edition.</p>
                <p className="font-sans text-base text-cream/75 mt-3 leading-relaxed max-w-3xl">
                  Join now and we start building immediately — your profile, cover shoot and first feature are prepared for
                  <b className="text-cream"> Issue Nº 1 on 1 September</b>. Your 12-month rotation runs from September, so you get
                  every month you're paying for. And you keep the <b className="text-cream">Founding Member</b> mark on your profile permanently.
                </p>
              </div>
            </Reveal>
          )}

          {/* WHY IT WORKS — the sales engine, spelled out */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="join-engine">
            {[
              ["Seen", "Your business appears throughout the magazine — cover, features, interview, advert, Directory, Noticeboard, newsletter and social — one fresh moment every month, all year."],
              ["Understood", "Every piece spells out what you do, what you sell and who you serve — written by the editorial desk to make readers want exactly that."],
              ["Chosen", "Your address, phone, email, website and offer sit on every feature, with structured data working underneath so Google and AI search engines recommend you too. Readers can buy, book or enquire on the spot."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={0.05 * i}>
                <div className="border border-cream/20 p-6 sm:p-7 h-full">
                  <p className="font-serif text-3xl text-gloss-gold tracking-tight">{i + 1}. {t}</p>
                  <p className="font-sans text-sm text-cream/70 mt-3 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="font-serif italic text-xl sm:text-2xl text-cream/80 mt-8 max-w-3xl">
              This isn't a nice-to-have. It's your visibility, marketing and sales presence — packaged as the most beautiful magazine in UK business.
            </p>
          </Reveal>

          <div id="plans" className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-16 scroll-mt-24">
            {/* Plan selector */}
            <Reveal className="border border-cream/20 p-8 sm:p-10">
              <p className="overline text-cream/50 mb-7">Choose your plan</p>
              <div className="space-y-4">
                {[
                  { id: "monthly", price: "£30", per: "per month", note: "Billed monthly. Cancel anytime — coverage ends with membership." },
                  { id: "yearly", price: "£300", per: "per year", note: "Two months free. Best value." },
                ].map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPlan(p.id)}
                    className={`w-full text-left p-6 border transition-colors ${plan === p.id ? "border-gloss-gold bg-cream/5" : "border-cream/20 hover:border-cream/50"}`}
                    data-testid={`plan-${p.id}`}
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="flex items-baseline gap-3">
                        <span className="font-serif text-4xl">{p.price}</span>
                        <span className="font-mono text-xs uppercase tracking-wider text-cream/60">{p.per}</span>
                      </div>
                      <span className={`w-5 h-5 border ${plan === p.id ? "bg-gloss-gold border-gloss-gold" : "border-cream/40"}`} />
                    </div>
                    <p className="font-sans text-sm text-cream/60 mt-2">{p.note}</p>
                    {p.id === "yearly" && <span className="inline-block mt-3 overline bg-gloss-gold text-ink px-2.5 py-1">Save £60</span>}
                  </button>
                ))}
              </div>
              <label className="flex items-start gap-3 mt-6 cursor-pointer select-none" data-testid="terms-agree-label">
                <input type="checkbox" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#c8a24a]" data-testid="terms-agree-checkbox" />
                <span className="font-sans text-sm text-cream/70">
                  I agree to the <a href="/membership-terms" target="_blank" rel="noreferrer" className="underline text-gloss-gold hover:text-cream">Membership Terms</a> — including that my coverage is produced and published by the S/ME studio.
                </span>
              </label>
              <button
                onClick={startCheckout}
                disabled={loadingCheckout || !agreedTerms}
                className="w-full mt-5 bg-cream text-ink py-5 overline flex items-center justify-center gap-3 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60"
                data-testid="checkout-button"
              >
                {loadingCheckout ? "Redirecting…" : <>Join S/ME <ArrowRight size={16} /></>}
              </button>
              <p className="font-mono text-[11px] text-cream/40 mt-4 text-center">Secure payment via Stripe · Cancel anytime — your features and coverage end with your membership</p>
            </Reveal>

            {/* Perks */}
            <Reveal delay={0.1} className="lg:pt-4">
              <p className="overline text-gloss-gold mb-7">What you get</p>
              <ul className="space-y-5">
                {PERKS.map((p) => (
                  <li key={p} className="flex gap-4 items-start border-b border-cream/10 pb-5">
                    <Check size={20} className="text-gloss-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="font-sans text-lg leading-snug">{p}</span>
                  </li>
                ))}
              </ul>
              <p className="font-serif italic text-2xl text-cream/80 mt-8">
                "The magazine builds readers. Readers find members. Members get clients."
              </p>
            </Reveal>
          </div>

          {/* THE HONEST MATHS — year-value comparison */}
          <div className="mt-24 sm:mt-32 scroll-mt-24" id="honest-maths" data-testid="honest-maths">
            <Reveal>
              <p className="overline text-gloss-gold mb-4">The honest maths</p>
              <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-[0.95] max-w-3xl">
                What your year actually buys — line by line.
              </h2>
              <p className="font-sans text-lg text-cream/70 mt-5 max-w-2xl leading-relaxed">
                Think in years, not months. Here's what a member receives across twelve months, next to what each piece
                costs at realistic UK market rates if you bought it separately.
              </p>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="mt-10 border border-cream/20">
                <div className="hidden sm:grid grid-cols-12 gap-6 px-5 sm:px-7 py-3 border-b border-cream/20 bg-cream/5">
                  <p className="col-span-7 overline text-cream/50">Included in membership</p>
                  <p className="col-span-3 overline text-cream/50">Bought separately</p>
                  <p className="col-span-2 overline text-cream/50 sm:text-right">Market rate</p>
                </div>
                {VALUE_STACK.map((v) => (
                  <div key={v.item} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-6 px-5 sm:px-7 py-4 border-b border-cream/10 items-baseline">
                    <div className="sm:col-span-7 flex gap-3 items-start">
                      <Check size={16} className="text-gloss-gold shrink-0 mt-1" strokeWidth={1.5} />
                      <div>
                        <p className="font-serif text-lg sm:text-xl tracking-tight">{v.item}</p>
                        <p className="font-sans text-sm text-cream/55">{v.detail}</p>
                      </div>
                    </div>
                    <p className="sm:col-span-3 font-mono text-xs uppercase tracking-wider text-cream/45 pl-7 sm:pl-0">{v.basis}</p>
                    <p className="sm:col-span-2 font-serif text-xl sm:text-right text-cream/90 pl-7 sm:pl-0">{v.market}</p>
                  </div>
                ))}
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 px-5 sm:px-7 py-6 items-baseline border-b border-cream/20">
                  <p className="sm:col-span-10 font-serif text-2xl tracking-tight text-cream/80">A conservative basket, bought separately, per year</p>
                  <p className="sm:col-span-2 font-serif text-3xl sm:text-right text-gloss-gold" data-testid="value-total">{VALUE_TOTAL}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 px-5 sm:px-7 py-6 items-baseline bg-cream text-ink">
                  <p className="sm:col-span-10 font-serif text-2xl tracking-tight">S/ME membership, everything above, per year</p>
                  <p className="sm:col-span-2 font-serif text-3xl sm:text-right" data-testid="sme-price">£300</p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="font-serif italic text-2xl sm:text-3xl text-cream/85 mt-10 max-w-3xl leading-snug">
                And the honest kicker: most of this bundle doesn't exist as a product anywhere else. You can't actually buy the equivalent.
              </p>
              <Link to="/media-kit" className="inline-flex items-center gap-2 mt-6 overline text-gloss-gold link-underline" data-testid="join-media-kit-link">
                Get this as a shareable one-pager →
              </Link>
            </Reveal>
          </div>

          {/* one magazine, the whole country */}
          <div className="mt-24 border-t border-cream/15 pt-14 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6" data-testid="join-whole-country">
            <Reveal>
              <p className="overline text-gloss-gold mb-4">One magazine, the whole country</p>
              <h3 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">Wherever you are, readers can reach you.</h3>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="font-sans text-base text-cream/75 leading-relaxed">
                S/ME is one national magazine, not local editions. Many members are destination businesses worth travelling
                for; many are remote or online businesses serving customers anywhere in the UK. Your area appears on your
                profile as context — never a boundary. One membership puts you in front of the whole country.
              </p>
            </Reveal>
          </div>

          {/* FAQ */}
          <Reveal>
            <JoinFaq />
          </Reveal>

          {/* Book a discovery call */}
          <div className="mt-24 scroll-mt-24" id="book-call">
            <Reveal>
              <BookCall />
            </Reveal>
          </div>
        </div>
      </div>
    </MagazineLayout>
  );
}
